import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dumbbell, Plus, Edit, Trash2, Search, X, ChevronDown, ChevronUp, ChevronRight,
  Eye, GripVertical, Zap, Video, CheckSquare, Square, ChevronLeft, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { useViewPreferences, ViewControls, sortItems, BulkDeleteModal, SelectionHeader, API } from "./SharedComponents";
import { SubtabManager, SubtabSelector } from "./SubtabManager";

// Reusable inline "Add Custom" input for any section's option chips
const AddCustomInput = ({ onAdd, placeholder, inputType = "text" }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [value, setValue] = useState("");
  const handleAdd = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onAdd(inputType === "number" ? parseFloat(trimmed) : trimmed);
    setValue("");
    setIsAdding(false);
  };
  if (!isAdding) {
    return (
      <button
        type="button"
        onClick={() => setIsAdding(true)}
        className="px-3 py-2 rounded-lg text-sm font-medium border-2 border-dashed border-[#2D3B4F] text-gray-500 hover:text-white hover:border-[#475569] transition-colors"
        data-testid="add-custom-btn"
      >
        + Custom
      </button>
    );
  }
  return (
    <div className="flex gap-1 items-center">
      <input
        type={inputType}
        step={inputType === "number" ? "any" : undefined}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
        placeholder={placeholder}
        className="w-24 bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-2 py-1.5 text-white text-sm"
        autoFocus
      />
      <button type="button" onClick={handleAdd} className="px-2 py-1.5 bg-primary text-white rounded-lg text-xs font-medium">Add</button>
      <button type="button" onClick={() => { setIsAdding(false); setValue(""); }} className="p-1.5 text-gray-400 hover:text-white">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

// Default options for each form type
const STRENGTH_DEFAULTS = {
  rm_options: Array.from({ length: 12 }, (_, i) => i + 1),
  equipment_options: ["Straight Bar", "Safety Squat Bar", "Dumbbell", "Cable"],
  category_options: ["Squat", "Single Legged", "Hinge", "Push", "Pull", "Rotate", "Brace"],
  tpv_options: [500, 450, 400, 350, 300, 250, 200, 150, 100],
  pv_options: [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0],
  av_options: [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0],
};
const SPEED_POWER_DEFAULTS = {
  fly_in_options: [5, 10, 15, 20, 25, 30, 35, 40, 45, 60],
  stance_options: [
    { value: "2_point", label: "2 Point Stance" },
    { value: "3_point", label: "3 Point Stance" },
    { value: "4_point", label: "4 Point Stance" },
    { value: "2_point_sideways", label: "2 Point Sideways Stance" },
  ],
  category_options: ["Sprint", "Jump", "Throw", "Land"],
  movement_categories: [
    { value: "sprint", label: "Sprint" },
    { value: "jump", label: "Jump" },
    { value: "throw", label: "Throw" },
    { value: "agility", label: "Agility" },
    { value: "reaction", label: "Reaction" },
  ],
  tpv_options: [500, 450, 400, 350, 300, 250, 200, 150, 100],
  pv_options: [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0],
  av_options: [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0],
};

// Hook to load and save exercise form option configs
const useExerciseFormConfig = (formType) => {
  const [loaded, setLoaded] = useState(false);
  const defaults = formType === "strength" ? STRENGTH_DEFAULTS : SPEED_POWER_DEFAULTS;
  const [config, setConfig] = useState(defaults);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/coach/exercise-form-config`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          const saved = data[formType];
          if (saved && Object.keys(saved).length > 0) {
            setConfig({ ...defaults, ...saved });
          }
        }
      } catch (e) { /* use defaults */ }
      setLoaded(true);
    };
    load();
  }, [formType]);

  const saveConfig = useCallback(async (newConfig) => {
    setConfig(newConfig);
    try {
      // Load existing config first to preserve other form type's data
      const res = await fetch(`${API}/coach/exercise-form-config`, { credentials: "include" });
      let existing = {};
      if (res.ok) {
        const data = await res.json();
        existing = { strength: data.strength || {}, speed_power: data.speed_power || {} };
      }
      existing[formType] = newConfig;
      await fetch(`${API}/coach/exercise-form-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(existing),
      });
    } catch (e) { /* silent fail, local state already updated */ }
  }, [formType]);

  return { config, saveConfig, loaded };
};

// ==================== EXERCISE LIBRARY ====================
const ExerciseLibrary = () => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [filters, setFilters] = useState({ muscle_group: "", difficulty: "", search: "", category: "" });
  const [subtabFilter, setSubtabFilter] = useState("");
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const { viewMode, setViewMode, sortMode, setSortMode } = useViewPreferences("exercises");
  
  // Pagination state - support 1000+ exercises
  const [currentPage, setCurrentPage] = useState(1);
  const [totalExercises, setTotalExercises] = useState(0);
  const ITEMS_PER_PAGE = 50;
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Duplicate detection state
  const [duplicateModal, setDuplicateModal] = useState({ show: false, newExercise: null, existingExercise: null });

  useEffect(() => {
    fetchExercises();
    fetchReferenceData();
  }, [filters.muscle_group, filters.difficulty, filters.category, subtabFilter, currentPage]);

  const fetchReferenceData = async () => {
    try {
      const [mgRes, eqRes] = await Promise.all([
        fetch(`${API}/coach/reference/muscle-groups`, { credentials: "include" }),
        fetch(`${API}/coach/reference/equipment`, { credentials: "include" })
      ]);
      if (mgRes.ok) setMuscleGroups((await mgRes.json()).muscle_groups);
      if (eqRes.ok) setEquipment((await eqRes.json()).equipment);
    } catch (error) {
      console.error("Failed to fetch reference data:", error);
    }
  };

  const fetchExercises = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.muscle_group) params.append("muscle_group", filters.muscle_group);
      if (filters.difficulty) params.append("difficulty", filters.difficulty);
      if (filters.category) params.append("category", filters.category);
      if (subtabFilter) params.append("subtab", subtabFilter);
      if (filters.search) params.append("search", filters.search);
      // Pagination params
      params.append("skip", ((currentPage - 1) * ITEMS_PER_PAGE).toString());
      params.append("limit", ITEMS_PER_PAGE.toString());

      const response = await fetch(`${API}/coach/exercises?${params}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setExercises(data.exercises);
        setTotalExercises(data.total || data.exercises.length);
      }
    } catch (error) {
      toast.error("Failed to load exercises");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page on search
    fetchExercises();
  };

  const handleDelete = async (exerciseId) => {
    if (!confirm("Are you sure you want to delete this exercise?")) return;
    try {
      const response = await fetch(`${API}/coach/exercises/${exerciseId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Exercise deleted");
        setSelectedIds(prev => { const next = new Set(prev); next.delete(exerciseId); return next; });
        fetchExercises();
      }
    } catch (error) {
      toast.error("Failed to delete exercise");
    }
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === exercises.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(exercises.map(e => e.exercise_id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        const response = await fetch(`${API}/coach/exercises/${id}`, {
          method: "DELETE",
          credentials: "include"
        });
        if (response.ok) successCount++;
      } catch (error) {
        console.error(`Failed to delete exercise ${id}:`, error);
      }
    }
    setBulkDeleting(false);
    setShowBulkDeleteModal(false);
    setSelectedIds(new Set());
    toast.success(`Deleted ${successCount} exercise${successCount !== 1 ? 's' : ''}`);
    fetchExercises();
  };

  const selectedExercises = exercises.filter(e => selectedIds.has(e.exercise_id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold">Exercise Library</h2>
          <p className="text-gray-400">Create and manage your exercise database</p>
        </div>
        <div className="flex items-center gap-4">
          <ViewControls 
            viewMode={viewMode} 
            setViewMode={setViewMode}
            sortMode={sortMode}
            setSortMode={setSortMode}
          />
          <button
            onClick={() => { setEditingExercise(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
            data-testid="create-exercise-btn"
          >
            <Plus className="w-5 h-5" />
            Add Exercise
          </button>
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { value: "", label: "All" },
          { value: "speed_power", label: "Speed" },
          { value: "strength", label: "Strength" },
          { value: "stamina", label: "Stamina" },
          { value: "suppleness", label: "Suppleness" },
          { value: "skill", label: "Skill" },
          { value: "extras", label: "Extras" },
        ].map((cat) => (
          <button
            key={cat.value}
            onClick={() => { setFilters({ ...filters, category: cat.value }); setSubtabFilter(""); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filters.category === cat.value
                ? "bg-primary text-white"
                : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"
            }`}
            data-testid={`filter-category-${cat.value || 'all'}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Subtabs (only shown when a category is selected) */}
      <SubtabManager
        libraryType="exercises"
        selectedCategory={filters.category}
        selectedSubtab={subtabFilter}
        onSubtabSelect={setSubtabFilter}
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-[#141B2D] border border-[#1E293B] rounded-xl p-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search exercises..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg pl-10 pr-4 py-2 text-white"
              data-testid="exercise-search"
            />
          </div>
        </div>
        <select
          value={filters.muscle_group}
          onChange={(e) => setFilters({ ...filters, muscle_group: e.target.value })}
          className="bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2 text-white"
          data-testid="filter-muscle-group"
        >
          <option value="">All Muscle Groups</option>
          {muscleGroups.map(mg => (
            <option key={mg} value={mg} className="capitalize">{mg.replace("_", " ")}</option>
          ))}
        </select>
        <select
          value={filters.difficulty}
          onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}
          className="bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2 text-white"
          data-testid="filter-difficulty"
        >
          <option value="">All Difficulties</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>

      {/* Selection Header */}
      {exercises.length > 0 && (
        <SelectionHeader
          selectedCount={selectedIds.size}
          totalCount={exercises.length}
          onSelectAll={selectAll}
          onClearSelection={clearSelection}
          onDeleteSelected={() => setShowBulkDeleteModal(true)}
          allSelected={selectedIds.size === exercises.length && exercises.length > 0}
        />
      )}

      {/* Exercise List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : exercises.length > 0 ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortItems(exercises, sortMode, "name").map((exercise) => (
              <ExerciseCard
                key={exercise.exercise_id}
                exercise={exercise}
                selected={selectedIds.has(exercise.exercise_id)}
                onToggleSelect={() => toggleSelect(exercise.exercise_id)}
                onEdit={() => { setEditingExercise(exercise); setShowForm(true); }}
                onDelete={() => handleDelete(exercise.exercise_id)}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {sortItems(exercises, sortMode, "name").map((exercise) => (
              <ExerciseListItem
                key={exercise.exercise_id}
                exercise={exercise}
                selected={selectedIds.has(exercise.exercise_id)}
                onToggleSelect={() => toggleSelect(exercise.exercise_id)}
                onEdit={() => { setEditingExercise(exercise); setShowForm(true); }}
                onDelete={() => handleDelete(exercise.exercise_id)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-12 bg-[#141B2D] border border-[#1E293B] rounded-xl">
          <Dumbbell className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">No exercises found</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 text-primary hover:underline"
          >
            Create your first exercise
          </button>
        </div>
      )}

      {/* Pagination Controls */}
      {totalExercises > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between bg-[#141B2D] border border-[#1E293B] rounded-xl p-4 mt-4">
          <div className="text-sm text-gray-400">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalExercises)} of {totalExercises} exercises
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg bg-[#1E293B] hover:bg-[#2D3B4F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, Math.ceil(totalExercises / ITEMS_PER_PAGE)) }, (_, i) => {
                const totalPages = Math.ceil(totalExercises / ITEMS_PER_PAGE);
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                      currentPage === pageNum 
                        ? 'bg-primary text-white' 
                        : 'bg-[#1E293B] hover:bg-[#2D3B4F] text-gray-400'
                    }`}
                    data-testid={`pagination-page-${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalExercises / ITEMS_PER_PAGE), p + 1))}
              disabled={currentPage >= Math.ceil(totalExercises / ITEMS_PER_PAGE)}
              className="p-2 rounded-lg bg-[#1E293B] hover:bg-[#2D3B4F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="pagination-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      <AnimatePresence>
        {showBulkDeleteModal && (
          <BulkDeleteModal
            items={selectedExercises}
            itemType="Exercise"
            nameField="name"
            onConfirm={handleBulkDelete}
            onCancel={() => setShowBulkDeleteModal(false)}
            deleting={bulkDeleting}
          />
        )}
      </AnimatePresence>

      {/* Exercise Form Modal */}
      <AnimatePresence>
        {showForm && (
          <ExerciseForm
            exercise={editingExercise}
            muscleGroups={muscleGroups}
            equipment={equipment}
            onClose={() => { setShowForm(false); setEditingExercise(null); }}
            onSave={() => { setShowForm(false); setEditingExercise(null); fetchExercises(); }}
            onDuplicate={(newEx, existingEx) => setDuplicateModal({ show: true, newExercise: newEx, existingExercise: existingEx })}
          />
        )}
      </AnimatePresence>

      {/* Duplicate Exercise Modal */}
      <AnimatePresence>
        {duplicateModal.show && (
          <DuplicateExerciseModal
            newExercise={duplicateModal.newExercise}
            existingExercise={duplicateModal.existingExercise}
            onKeepExisting={() => {
              setDuplicateModal({ show: false, newExercise: null, existingExercise: null });
              toast.info("Kept existing exercise, new one was not created");
            }}
            onReplaceExisting={async () => {
              try {
                const response = await fetch(`${API}/coach/exercises/force`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    ...duplicateModal.newExercise,
                    delete_existing: true,
                    existing_exercise_id: duplicateModal.existingExercise.exercise_id
                  })
                });
                if (response.ok) {
                  toast.success("Replaced existing exercise with new one");
                  fetchExercises();
                } else {
                  toast.error("Failed to replace exercise");
                }
              } catch (error) {
                toast.error("Failed to replace exercise");
              }
              setDuplicateModal({ show: false, newExercise: null, existingExercise: null });
            }}
            onClose={() => setDuplicateModal({ show: false, newExercise: null, existingExercise: null })}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== DUPLICATE EXERCISE MODAL ====================
const DuplicateExerciseModal = ({ newExercise, existingExercise, onKeepExisting, onReplaceExisting, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-md p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-yellow-500" />
        </div>
        <div>
          <h3 className="font-heading text-xl font-bold">Duplicate Exercise Found</h3>
          <p className="text-gray-400 text-sm">An exercise with this name already exists</p>
        </div>
      </div>

      <div className="bg-[#141B2D] rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-400 mb-1">Exercise name:</p>
        <p className="font-semibold text-lg">{existingExercise?.name}</p>
        {existingExercise?.category && (
          <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded mt-2 inline-block">
            {existingExercise.category}
          </span>
        )}
      </div>

      <p className="text-gray-400 text-sm mb-6">
        Would you like to keep the existing exercise or replace it with the new one?
      </p>

      <div className="space-y-3">
        <button
          onClick={onKeepExisting}
          className="w-full bg-[#2D3B4F] hover:bg-[#374B69] text-white py-3 rounded-lg font-medium transition-colors"
          data-testid="keep-existing-btn"
        >
          Keep Existing (Cancel New)
        </button>
        <button
          onClick={onReplaceExisting}
          className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium transition-colors"
          data-testid="replace-existing-btn"
        >
          Delete Old & Create New
        </button>
      </div>
    </motion.div>
  </motion.div>
);

const ExerciseCard = ({ exercise, selected, onToggleSelect, onEdit, onDelete }) => (
  <div className={`bg-[#141B2D] border rounded-xl overflow-hidden hover:border-primary/50 transition-all ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-[#1E293B]'}`} data-testid={`exercise-card-${exercise.exercise_id}`}>
    <div className="aspect-video bg-[#1E293B] relative">
      {exercise.thumbnail_url ? (
        <img src={exercise.thumbnail_url} alt={exercise.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Dumbbell className="w-12 h-12 text-gray-500" />
        </div>
      )}
      {exercise.video_url && (
        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded flex items-center gap-1">
          <Video className="w-4 h-4" />
          <span className="text-xs">Video</span>
        </div>
      )}
      {/* Category Badge */}
      <div className={`absolute top-2 left-10 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 ${
        exercise.category === "speed_power" 
          ? "bg-lime-400 text-black" 
          : "bg-primary text-white"
      }`}>
        {exercise.category === "speed_power" ? (
          <><Zap className="w-3 h-3" /> Speed/Power</>
        ) : (
          <><Dumbbell className="w-3 h-3" /> Strength</>
        )}
      </div>
      {/* Selection Checkbox */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        className={`absolute top-2 left-2 w-6 h-6 rounded flex items-center justify-center transition-colors ${
          selected ? 'bg-primary text-white' : 'bg-black/60 hover:bg-black/80 text-white'
        }`}
        data-testid={`select-exercise-${exercise.exercise_id}`}
      >
        {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
      </button>
    </div>
    <div className="p-4">
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-heading text-lg font-bold">{exercise.name}</h3>
        <div className="flex gap-1">
          <button onClick={onEdit} className="p-1 hover:bg-[#1E293B] rounded" data-testid={`edit-exercise-${exercise.exercise_id}`}>
            <Edit className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="p-1 hover:bg-[#1E293B] rounded text-red-400" data-testid={`delete-exercise-${exercise.exercise_id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-3 line-clamp-2">{exercise.description}</p>
      <div className="flex flex-wrap gap-2">
        <span className="text-xs bg-[#2D3B4F] text-gray-400 px-2 py-1 rounded capitalize">
          {exercise.exercise_type?.replace("_", " ")}
        </span>
        {exercise.muscle_groups?.slice(0, 2).map((mg, i) => (
          <span key={i} className="text-xs bg-primary/20 text-primary px-2 py-1 rounded capitalize">
            {mg.replace("_", " ")}
          </span>
        ))}
        <span className={`text-xs px-2 py-1 rounded capitalize ${
          exercise.difficulty === "beginner" ? "bg-emerald-500/20 text-emerald-400" :
          exercise.difficulty === "advanced" ? "bg-red-500/20 text-red-400" :
          "bg-amber-500/20 text-amber-400"
        }`}>
          {exercise.difficulty}
        </span>
      </div>
    </div>
  </div>
);

// Exercise List Item (for list view)
const ExerciseListItem = ({ exercise, selected, onToggleSelect, onEdit, onDelete }) => (
  <div className={`bg-[#141B2D] border rounded-lg p-4 hover:border-primary/50 transition-all flex items-center gap-4 ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-[#1E293B]'}`} data-testid={`exercise-list-${exercise.exercise_id}`}>
    {/* Selection Checkbox */}
    <button
      onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
      className={`w-6 h-6 rounded flex items-center justify-center transition-colors flex-shrink-0 ${
        selected ? 'bg-primary text-white' : 'bg-[#1E293B] hover:bg-[#2D3B4F] text-white'
      }`}
      data-testid={`select-exercise-list-${exercise.exercise_id}`}
    >
      {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
    </button>
    
    {/* Thumbnail */}
    <div className="w-16 h-16 bg-[#1E293B] rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
      {exercise.thumbnail_url ? (
        <img src={exercise.thumbnail_url} alt={exercise.name} className="w-full h-full object-cover" />
      ) : (
        <Dumbbell className="w-6 h-6 text-gray-500" />
      )}
    </div>
    
    {/* Info */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-semibold text-white truncate">{exercise.name}</h3>
        {exercise.video_url && <Video className="w-4 h-4 text-gray-500 flex-shrink-0" />}
      </div>
      <div className="flex flex-wrap gap-2">
        <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
          exercise.category === "speed_power" 
            ? "bg-lime-400/20 text-lime-300" 
            : "bg-primary/20 text-primary"
        }`}>
          {exercise.category === "speed_power" ? <Zap className="w-3 h-3" /> : <Dumbbell className="w-3 h-3" />}
          {exercise.category === "speed_power" ? "Speed/Power" : "Strength"}
        </span>
        {exercise.muscle_groups?.slice(0, 2).map((mg, i) => (
          <span key={i} className="text-xs bg-[#2D3B4F] text-gray-400 px-2 py-0.5 rounded capitalize">
            {mg.replace("_", " ")}
          </span>
        ))}
        <span className={`text-xs px-2 py-0.5 rounded capitalize ${
          exercise.difficulty === "beginner" ? "bg-emerald-500/20 text-emerald-400" :
          exercise.difficulty === "advanced" ? "bg-red-500/20 text-red-400" :
          "bg-amber-500/20 text-amber-400"
        }`}>
          {exercise.difficulty}
        </span>
      </div>
    </div>
    
    {/* Actions */}
    <div className="flex items-center gap-2 flex-shrink-0">
      <button onClick={onEdit} className="p-2 hover:bg-[#1E293B] rounded-lg" data-testid={`edit-exercise-list-${exercise.exercise_id}`}>
        <Edit className="w-4 h-4" />
      </button>
      <button onClick={onDelete} className="p-2 hover:bg-[#1E293B] rounded-lg text-red-400" data-testid={`delete-exercise-list-${exercise.exercise_id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// ==================== NUMBER INPUT WITH ARROWS ====================
const NumberInput = ({ value, onChange, min = 0, max = 100, label }) => (
  <div className="flex items-center gap-2">
    {label && <span className="text-sm text-gray-400">{label}</span>}
    <div className="flex items-center bg-[#0B0F1A] border border-[#1E293B] rounded-lg">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="px-3 py-2 hover:bg-[#2D3B4F] rounded-l-lg transition-colors"
      >
        <ChevronDown className="w-4 h-4" />
      </button>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.min(max, Math.max(min, parseInt(e.target.value) || min)))}
        className="w-16 bg-transparent text-center text-white py-2 outline-none"
        min={min}
        max={max}
      />
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="px-3 py-2 hover:bg-[#2D3B4F] rounded-r-lg transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-90" />
      </button>
    </div>
  </div>
);

// ==================== PREVIEW MODAL ====================
const PreviewModal = ({ exercises, onClose, onConfirm, saving }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sticky top-0 bg-[#141B2D] border-b border-[#1E293B] p-6 flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold">Preview Exercises</h2>
          <p className="text-gray-400 text-sm">{exercises.length} exercises will be created</p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6 max-h-[50vh] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {exercises.map((ex, i) => (
            <div key={i} className="bg-[#0B0F1A] border border-[#1E293B] rounded-lg p-3">
              <p className="font-medium text-white">{ex.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {ex.equipment} • {ex.movement_category}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-[#1E293B] p-6 flex gap-4">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors"
        >
          Back to Edit
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={saving}
          className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
        >
          {saving ? "Creating..." : `Create ${exercises.length} Exercises`}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// ==================== STRENGTH BATCH FORM ====================
const StrengthBatchForm = ({ onClose, onSave, onDuplicate }) => {
  const { config: savedConfig, saveConfig, loaded: configLoaded } = useExerciseFormConfig("strength");
  const [exerciseNames, setExerciseNames] = useState([""]);
  const [selectedRMs, setSelectedRMs] = useState([]);
  const [accelVelocityParams, setAccelVelocityParams] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [numberOfMaxes, setNumberOfMaxes] = useState(1);
  const [maxesConfig, setMaxesConfig] = useState([{ type: "weight_lbs" }]);
  const [videoUrl, setVideoUrl] = useState("");
  const [movementCategory, setMovementCategory] = useState("squat");
  const [namingOrder, setNamingOrder] = useState(1);
  const [namingConfig, setNamingConfig] = useState([{ section: "rm" }]);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [barEquipmentList, setBarEquipmentList] = useState([]); // from coach_equipment

  // Fetch bar equipment on mount
  useEffect(() => {
    const fetchBarEquipment = async () => {
      try {
        const res = await fetch(`${API}/coach/bar-equipment`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setBarEquipmentList(data.equipment || []);
        }
      } catch (e) { console.error("Failed to fetch bar equipment:", e); }
    };
    fetchBarEquipment();
  }, []);
  
  // Section order state - defines the order of form sections
  const [sectionOrder, setSectionOrder] = useState([
    "exercise_names",
    "rms",
    "accel_velocity",
    "equipment",
    "number_of_maxes",
    "video_url",
    "category",
    "naming_order"
  ]);

  // Move section up
  const moveSectionUp = (index) => {
    if (index === 0) return;
    const newOrder = [...sectionOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setSectionOrder(newOrder);
  };

  // Move section down
  const moveSectionDown = (index) => {
    if (index === sectionOrder.length - 1) return;
    const newOrder = [...sectionOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setSectionOrder(newOrder);
  };

  // Editable options - loaded from saved config
  const [rmOptions, setRmOptions] = useState(STRENGTH_DEFAULTS.rm_options);
  const [equipmentOptions, setEquipmentOptions] = useState(STRENGTH_DEFAULTS.equipment_options);
  const [categoryOptions, setCategoryOptions] = useState(STRENGTH_DEFAULTS.category_options);
  const [tpvOptions, setTpvOptions] = useState(STRENGTH_DEFAULTS.tpv_options);
  const [pvOptions, setPvOptions] = useState(STRENGTH_DEFAULTS.pv_options);
  const [avOptions, setAvOptions] = useState(STRENGTH_DEFAULTS.av_options);

  // Load saved config when it arrives
  useEffect(() => {
    if (configLoaded && savedConfig) {
      if (savedConfig.rm_options?.length) setRmOptions(savedConfig.rm_options);
      if (savedConfig.equipment_options?.length) setEquipmentOptions(savedConfig.equipment_options);
      if (savedConfig.category_options?.length) setCategoryOptions(savedConfig.category_options);
      if (savedConfig.tpv_options?.length) setTpvOptions(savedConfig.tpv_options);
      if (savedConfig.pv_options?.length) setPvOptions(savedConfig.pv_options);
      if (savedConfig.av_options?.length) setAvOptions(savedConfig.av_options);
    }
  }, [configLoaded, savedConfig]);

  // Auto-save whenever options change (after initial load)
  const persistOptions = useCallback(() => {
    if (!configLoaded) return;
    saveConfig({
      rm_options: rmOptions,
      equipment_options: equipmentOptions,
      category_options: categoryOptions,
      tpv_options: tpvOptions,
      pv_options: pvOptions,
      av_options: avOptions,
    });
  }, [configLoaded, rmOptions, equipmentOptions, categoryOptions, tpvOptions, pvOptions, avOptions, saveConfig]);

  useEffect(() => {
    if (configLoaded) persistOptions();
  }, [rmOptions, equipmentOptions, categoryOptions, tpvOptions, pvOptions, avOptions]);

  const maxTypeOptions = [
    { value: "weight_lbs", label: "Weight (in lbs)" },
    { value: "time_secs", label: "Time (in Secs)" },
    { value: "speed_mph", label: "Speed (in MPH)" },
    { value: "resistance", label: "Resistance" },
    { value: "height_inches", label: "Height (in Inches)" },
  ];
  const namingSections = [
    { value: "rm", label: "RM" },
    { value: "equipment", label: "Equipment" },
    { value: "exercise_name", label: "Exercise Name" },
    { value: "category", label: "Category" },
    { value: "accel_velocity", label: "Accel/Velocity" },
  ];

  // Update maxes config when number changes
  const handleMaxesCountChange = (count) => {
    setNumberOfMaxes(count);
    const newConfig = Array.from({ length: count }, (_, i) => 
      maxesConfig[i] || { type: "weight_lbs" }
    );
    setMaxesConfig(newConfig);
  };

  // Update naming config when order count changes
  const handleNamingOrderChange = (count) => {
    setNamingOrder(count);
    const newConfig = Array.from({ length: count }, (_, i) => 
      namingConfig[i] || { section: namingSections[i]?.value || "rm" }
    );
    setNamingConfig(newConfig);
  };

  // Toggle checkbox selection
  const toggleSelection = (arr, setArr, value) => {
    if (arr.includes(value)) {
      setArr(arr.filter(v => v !== value));
    } else {
      setArr([...arr, value]);
    }
  };

  // Add exercise name field
  const addExerciseName = () => {
    setExerciseNames([...exerciseNames, ""]);
  };

  // Update exercise name
  const updateExerciseName = (index, value) => {
    const updated = [...exerciseNames];
    updated[index] = value;
    setExerciseNames(updated);
  };

  // Remove exercise name
  const removeExerciseName = (index) => {
    if (exerciseNames.length > 1) {
      setExerciseNames(exerciseNames.filter((_, i) => i !== index));
    }
  };

  // Generate exercise name based on naming order
  const generateExerciseName = (baseName, rm, equipment, accelVelocity) => {
    const parts = [];
    namingConfig.forEach(config => {
      switch (config.section) {
        case "rm":
          if (rm) parts.push(`${rm}RM`);
          break;
        case "equipment":
          if (equipment) parts.push(equipment);
          break;
        case "exercise_name":
          if (baseName) parts.push(baseName);
          break;
        case "category":
          parts.push(movementCategory);
          break;
        case "accel_velocity":
          if (accelVelocity) parts.push(accelVelocity);
          break;
        default:
          break;
      }
    });
    return parts.join(" ");
  };

  // Generate all exercise combinations for preview
  const generateExercises = () => {
    const exercises = [];
    const validNames = exerciseNames.filter(n => n.trim());
    const rms = selectedRMs.length > 0 ? selectedRMs : [null];
    const equips = selectedEquipment.length > 0 ? selectedEquipment : [null];
    const accelVelocities = accelVelocityParams.length > 0 ? accelVelocityParams : [null];

    validNames.forEach(baseName => {
      rms.forEach(rm => {
        equips.forEach(equipment => {
          accelVelocities.forEach(accelVelocity => {
            const name = generateExerciseName(baseName, rm, equipment, accelVelocity);
            // Look up bar_equipment_id if this equipment matches a bar from the equipment list
            const matchedBar = barEquipmentList.find(b => b.name === equipment);
            exercises.push({
              name,
              base_name: baseName,
              rm: rm ? `${rm}RM` : null,
              equipment: equipment || "None",
              bar_equipment_id: matchedBar ? matchedBar.equipment_id : null,
              movement_category: movementCategory,
              accel_velocity_param: accelVelocity,
              maxes_config: maxesConfig,
              video_url: videoUrl,
              category: "strength",
              exercise_type: "strength",
            });
          });
        });
      });
    });

    return exercises;
  };

  // Handle preview click
  const handlePreview = () => {
    const validNames = exerciseNames.filter(n => n.trim());
    if (validNames.length === 0) {
      toast.error("Please add at least one exercise name");
      return;
    }
    setShowPreview(true);
  };

  // Handle create exercises
  const handleCreate = async () => {
    setSaving(true);
    const exercises = generateExercises();
    
    try {
      let successCount = 0;
      let duplicateCount = 0;
      let firstCreated = null;
      
      for (const exercise of exercises) {
        const exerciseData = {
          name: exercise.name,
          description: `${exercise.movement_category} exercise`,
          category: "strength",
          exercise_type: "strength",
          equipment: exercise.equipment ? [exercise.equipment.toLowerCase().replace(/ /g, "_")] : ["none"],
          video_url: exercise.video_url || null,
          muscle_groups: [],
          difficulty: "intermediate",
          rm_value: exercise.rm,
          movement_category: exercise.movement_category,
          accel_velocity_param: exercise.accel_velocity_param,
          maxes_config: exercise.maxes_config,
          bar_equipment_id: exercise.bar_equipment_id || null,
        };
        
        const response = await fetch(`${API}/coach/exercises`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(exerciseData)
        });
        
        if (response.ok) {
          successCount++;
          if (!firstCreated) {
            const created = await response.json();
            firstCreated = { exercise_id: created.exercise_id, name: created.name, rm_value: created.rm_value, bar_equipment_id: created.bar_equipment_id };
          }
        } else if (response.status === 409) {
          // Duplicate found
          duplicateCount++;
          const errorData = await response.json();
          if (!firstCreated && errorData.detail?.existing_exercise) {
            firstCreated = { exercise_id: errorData.detail.existing_exercise.exercise_id, name: errorData.detail.existing_exercise.name, rm_value: errorData.detail.existing_exercise.rm_value, bar_equipment_id: errorData.detail.existing_exercise.bar_equipment_id };
          }
          if (onDuplicate && errorData.detail?.existing_exercise) {
            // Show modal for the first duplicate, skip others
            if (duplicateCount === 1) {
              onDuplicate(exerciseData, errorData.detail.existing_exercise);
            }
          }
        }
      }
      
      if (successCount > 0) {
        toast.success(`Created ${successCount} exercise${successCount > 1 ? 's' : ''}!`);
      }
      if (duplicateCount > 0 && successCount > 0) {
        toast.warning(`${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''} skipped`);
      }
      
      setShowPreview(false);
      onSave(firstCreated);
    } catch (error) {
      toast.error("Failed to create exercises");
    } finally {
      setSaving(false);
    }
  };

  // Drag and drop state
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);

  // Handle drag start
  const handleDragStart = (e, sectionId, index) => {
    setDraggedSection({ id: sectionId, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sectionId);
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    setDraggedSection(null);
    setDragOverSection(null);
  };

  // Handle drag over
  const handleDragOver = (e, sectionId, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedSection && draggedSection.id !== sectionId) {
      setDragOverSection({ id: sectionId, index });
    }
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverSection(null);
  };

  // Handle drop
  const handleDrop = (e, targetSectionId, targetIndex) => {
    e.preventDefault();
    if (!draggedSection || draggedSection.id === targetSectionId) {
      setDraggedSection(null);
      setDragOverSection(null);
      return;
    }

    const newOrder = [...sectionOrder];
    const sourceIndex = draggedSection.index;
    
    // Remove from old position
    newOrder.splice(sourceIndex, 1);
    // Insert at new position
    const adjustedTargetIndex = targetIndex > sourceIndex ? targetIndex - 1 : targetIndex;
    newOrder.splice(adjustedTargetIndex, 0, draggedSection.id);
    
    setSectionOrder(newOrder);
    setDraggedSection(null);
    setDragOverSection(null);
  };

  // Get section title
  const getSectionTitle = (sectionId) => {
    const titles = {
      exercise_names: "Exercise Name(s)",
      rms: "RM's (Rep Maxes)",
      accel_velocity: "Accel/Velocity Parameters",
      equipment: "Equipment",
      number_of_maxes: "Number of Maxes",
      video_url: "Video URL",
      category: "Category (Movement Pattern)",
      naming_order: "Order of Naming"
    };
    return titles[sectionId] || sectionId;
  };

  // Render section content based on ID
  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case "exercise_names":
        return (
          <div className="space-y-2">
            {exerciseNames.map((name, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => updateExerciseName(i, e.target.value)}
                  placeholder="e.g., Box Squat (Parallel Depth)"
                  className="flex-1 bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white text-sm"
                  data-testid={`exercise-name-input-${i}`}
                />
                {exerciseNames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeExerciseName(i)}
                    className="p-2.5 hover:bg-[#1E293B] rounded-lg text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        );

      case "rms":
        return (
          <div className="flex flex-wrap gap-2">
            {rmOptions.map((rm) => (
              <div key={rm} className="relative group inline-flex">
                <button
                  type="button"
                  onClick={() => toggleSelection(selectedRMs, setSelectedRMs, rm)}
                  className={`w-12 h-10 rounded-lg text-sm font-medium transition-colors ${
                    selectedRMs.includes(rm)
                      ? "bg-primary text-white"
                      : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"
                  }`}
                >
                  {rm}RM
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setRmOptions(rmOptions.filter(r => r !== rm)); setSelectedRMs(selectedRMs.filter(r => r !== rm)); }}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10"
                  title="Remove option"
                >x</button>
              </div>
            ))}
            <AddCustomInput
              onAdd={(val) => { const num = parseInt(val); if (num > 0 && !rmOptions.includes(num)) setRmOptions([...rmOptions, num].sort((a, b) => a - b)); }}
              placeholder="e.g. 15"
              inputType="number"
            />
          </div>
        );

      case "accel_velocity":
        return (
          <div className="space-y-4">
            {/* TpV - Time to Peak Velocity */}
            <div>
              <p className="text-xs text-gray-500 mb-2 font-medium">TpV (Time to Peak Velocity)</p>
              <div className="flex flex-wrap gap-2">
                {tpvOptions.map((ms) => {
                  const tpvValue = `TpV ${ms}ms`;
                  const isSelected = accelVelocityParams.includes(tpvValue);
                  return (
                    <div key={ms} className="relative group inline-flex">
                      <button type="button"
                        onClick={() => { isSelected ? setAccelVelocityParams(accelVelocityParams.filter(p => p !== tpvValue)) : setAccelVelocityParams([...accelVelocityParams, tpvValue]); }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-lime-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                        data-testid={`tpv-btn-${ms}`}
                      >TpV {ms}ms</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setTpvOptions(tpvOptions.filter(v => v !== ms)); setAccelVelocityParams(accelVelocityParams.filter(p => p !== tpvValue)); }}
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                    </div>
                  );
                })}
                <AddCustomInput
                  onAdd={(val) => { const num = parseInt(val); if (num > 0 && !tpvOptions.includes(num)) setTpvOptions([...tpvOptions, num].sort((a, b) => b - a)); }}
                  placeholder="ms" inputType="number"
                />
              </div>
            </div>
            {/* pV - Peak Velocity */}
            <div>
              <p className="text-xs text-gray-500 mb-2 font-medium">pV (Peak Velocity)</p>
              <div className="flex flex-wrap gap-2">
                {pvOptions.map((val) => {
                  const pvValue = `pV ${val}m/s`;
                  const isSelected = accelVelocityParams.includes(pvValue);
                  return (
                    <div key={val} className="relative group inline-flex">
                      <button type="button"
                        onClick={() => { isSelected ? setAccelVelocityParams(accelVelocityParams.filter(p => p !== pvValue)) : setAccelVelocityParams([...accelVelocityParams, pvValue]); }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-cyan-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                        data-testid={`pv-btn-${val}`}
                      >pV {val}m/s</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setPvOptions(pvOptions.filter(v => v !== val)); setAccelVelocityParams(accelVelocityParams.filter(p => p !== pvValue)); }}
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                    </div>
                  );
                })}
                <AddCustomInput
                  onAdd={(val) => { const num = parseFloat(val); if (num > 0 && !pvOptions.includes(num)) setPvOptions([...pvOptions, num].sort((a, b) => a - b)); }}
                  placeholder="m/s" inputType="number"
                />
              </div>
            </div>
            {/* aV - Average Velocity */}
            <div>
              <p className="text-xs text-gray-500 mb-2 font-medium">aV (Average Velocity)</p>
              <div className="flex flex-wrap gap-2">
                {avOptions.map((val) => {
                  const avValue = `aV ${val}m/s`;
                  const isSelected = accelVelocityParams.includes(avValue);
                  return (
                    <div key={val} className="relative group inline-flex">
                      <button type="button"
                        onClick={() => { isSelected ? setAccelVelocityParams(accelVelocityParams.filter(p => p !== avValue)) : setAccelVelocityParams([...accelVelocityParams, avValue]); }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-orange-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                        data-testid={`av-btn-${val}`}
                      >aV {val}m/s</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setAvOptions(avOptions.filter(v => v !== val)); setAccelVelocityParams(accelVelocityParams.filter(p => p !== avValue)); }}
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                    </div>
                  );
                })}
                <AddCustomInput
                  onAdd={(val) => { const num = parseFloat(val); if (num > 0 && !avOptions.includes(num)) setAvOptions([...avOptions, num].sort((a, b) => a - b)); }}
                  placeholder="m/s" inputType="number"
                />
              </div>
            </div>
          </div>
        );

      case "equipment":
        return (
          <div className="space-y-3">
            {barEquipmentList.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2">Bar Equipment (with plate calculations)</p>
                <div className="flex flex-wrap gap-2">
                  {barEquipmentList.map((bar) => (
                    <button key={bar.equipment_id} type="button"
                      onClick={() => toggleSelection(selectedEquipment, setSelectedEquipment, bar.name)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedEquipment.includes(bar.name) ? "bg-lime-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                      data-testid={`equipment-bar-${bar.equipment_id}`}
                    >{bar.name} <span className="text-xs opacity-70">({bar.weight} lbs)</span></button>
                  ))}
                </div>
              </div>
            )}
            <div>
              {barEquipmentList.length > 0 && <p className="text-xs text-gray-500 mb-2">Other Equipment (no plate calc)</p>}
              <div className="flex flex-wrap gap-2">
                {equipmentOptions.filter(eq => !barEquipmentList.some(b => b.name === eq)).map((eq) => (
                  <div key={eq} className="relative group inline-flex">
                    <button type="button"
                      onClick={() => toggleSelection(selectedEquipment, setSelectedEquipment, eq)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedEquipment.includes(eq) ? "bg-lime-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                    >{eq}</button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setEquipmentOptions(equipmentOptions.filter(o => o !== eq)); setSelectedEquipment(selectedEquipment.filter(o => o !== eq)); }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                  </div>
                ))}
                <AddCustomInput
                  onAdd={(val) => { if (val && !equipmentOptions.includes(val)) setEquipmentOptions([...equipmentOptions, val]); }}
                  placeholder="e.g. Kettlebell"
                />
              </div>
            </div>
          </div>
        );

      case "number_of_maxes":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {maxesConfig.map((config, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-8">#{i + 1}</span>
                <select
                  value={config.type}
                  onChange={(e) => {
                    const updated = [...maxesConfig];
                    updated[i] = { type: e.target.value };
                    setMaxesConfig(updated);
                  }}
                  className="flex-1 bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white"
                >
                  {maxTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        );

      case "video_url":
        return (
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://youtube.com/..."
            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white text-sm"
          />
        );

      case "category":
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {categoryOptions.map((cat) => (
                <div key={cat} className="relative group inline-flex">
                  <button type="button"
                    onClick={() => setMovementCategory(cat.toLowerCase().replace(/ /g, "_"))}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${movementCategory === cat.toLowerCase().replace(/ /g, "_") ? "bg-primary text-white" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                  >{cat}</button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setCategoryOptions(categoryOptions.filter(c => c !== cat)); }}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                </div>
              ))}
              <AddCustomInput
                onAdd={(val) => { if (val && !categoryOptions.includes(val)) { setCategoryOptions([...categoryOptions, val]); setMovementCategory(val.toLowerCase().replace(/ /g, "_")); } }}
                placeholder="e.g. Olympic"
              />
            </div>
          </div>
        );

      case "naming_order":
        return (
          <>
            <p className="text-xs text-gray-500 mb-3">Select the order of how exercise names will be generated</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {namingConfig.map((config, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-8">#{i + 1}</span>
                  <select
                    value={config.section}
                    onChange={(e) => {
                      const updated = [...namingConfig];
                      updated[i] = { section: e.target.value };
                      setNamingConfig(updated);
                    }}
                    className="flex-1 bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white"
                  >
                    {namingSections.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-[#141B2D] rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Example output:</p>
              <p className="text-sm text-primary font-medium">
                {generateExerciseName(
                  exerciseNames[0] || "Box Squat",
                  selectedRMs[0] || 1,
                  selectedEquipment[0] || "Straight Bar",
                  accelVelocityParams[0] || null
                )}
              </p>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Get right content for section header
  const getSectionRightContent = (sectionId) => {
    switch (sectionId) {
      case "exercise_names":
        return (
          <button
            type="button"
            onClick={addExerciseName}
            className="flex items-center gap-1 text-xs bg-primary/20 text-primary px-3 py-1.5 rounded-lg hover:bg-primary/30 transition-colors"
          >
            <Plus className="w-3 h-3" /> Add Exercise
          </button>
        );
      case "number_of_maxes":
        return (
          <NumberInput 
            value={numberOfMaxes} 
            onChange={handleMaxesCountChange} 
            min={1} 
            max={10} 
          />
        );
      case "naming_order":
        return (
          <NumberInput 
            value={namingOrder} 
            onChange={handleNamingOrderChange} 
            min={1} 
            max={5} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Render sections in dynamic order */}
        {sectionOrder.map((sectionId, index) => (
          <div 
            key={sectionId}
            className={`bg-[#0F1419] border rounded-xl p-4 transition-all duration-200 ${
              dragOverSection?.id === sectionId 
                ? 'border-primary border-2 scale-[1.01]' 
                : draggedSection?.id === sectionId
                  ? 'border-[#2D3B4F] opacity-50'
                  : 'border-[#1E293B]'
            }`}
            data-testid={`section-${sectionId}`}
            draggable="true"
            onDragStart={(e) => handleDragStart(e, sectionId, index)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, sectionId, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, sectionId, index)}
          >
            <div className="flex items-start gap-3">
              {/* Drag Handle + Reorder Arrows */}
              <div className="flex items-center gap-2">
                {/* Drag Handle */}
                <div 
                  className="cursor-grab active:cursor-grabbing p-1 text-gray-500 hover:text-white transition-colors"
                  title="Drag to reorder"
                >
                  <GripVertical className="w-5 h-5" />
                </div>
                
                {/* Reorder Arrows */}
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveSectionUp(index); }}
                    disabled={index === 0}
                    className={`p-0.5 rounded transition-colors ${
                      index === 0 
                        ? "text-[#475569] cursor-not-allowed" 
                        : "text-gray-400 hover:text-white hover:bg-[#2D3B4F]"
                    }`}
                    data-testid={`move-up-${sectionId}`}
                    title="Move section up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveSectionDown(index); }}
                    disabled={index === sectionOrder.length - 1}
                    className={`p-0.5 rounded transition-colors ${
                      index === sectionOrder.length - 1 
                        ? "text-[#475569] cursor-not-allowed" 
                        : "text-gray-400 hover:text-white hover:bg-[#2D3B4F]"
                    }`}
                    data-testid={`move-down-${sectionId}`}
                    title="Move section down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Section Content */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-white">{getSectionTitle(sectionId)}</label>
                  {getSectionRightContent(sectionId)}
                </div>
                {renderSectionContent(sectionId)}
              </div>
            </div>
          </div>
        ))}

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4 border-t border-[#1E293B]">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePreview}
            className="flex-1 bg-[#2D3B4F] text-white py-3 rounded-lg font-semibold hover:bg-[#374B69] transition-colors flex items-center justify-center gap-2"
          >
            <Eye className="w-5 h-5" /> Preview
          </button>
          <button
            type="button"
            onClick={handlePreview}
            className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
          >
            Create Exercises
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && (
          <PreviewModal
            exercises={generateExercises()}
            onClose={() => setShowPreview(false)}
            onConfirm={handleCreate}
            saving={saving}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== STRENGTH EDIT FORM (Single Exercise) ====================
const StrengthEditForm = ({ exercise, onClose, onSave }) => {
  const { config: savedConfig, saveConfig, loaded: configLoaded } = useExerciseFormConfig("strength");

  // Editable options - loaded from saved config
  const [rmOptions, setRmOptions] = useState(STRENGTH_DEFAULTS.rm_options.map(n => `${n}RM`));
  const [equipmentOptions, setEquipmentOptions] = useState(["None", ...STRENGTH_DEFAULTS.equipment_options]);
  const [categoryOptions, setCategoryOptions] = useState(STRENGTH_DEFAULTS.category_options);
  const [tpvOptions, setTpvOptions] = useState(STRENGTH_DEFAULTS.tpv_options);
  const [pvOptions, setPvOptions] = useState(STRENGTH_DEFAULTS.pv_options);
  const [avOptions, setAvOptions] = useState(STRENGTH_DEFAULTS.av_options);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Load saved config (only once on initial load)
  useEffect(() => {
    if (configLoaded && savedConfig && !initialLoadDone) {
      if (savedConfig.rm_options?.length) setRmOptions(savedConfig.rm_options.map(n => `${n}RM`));
      if (savedConfig.equipment_options?.length) setEquipmentOptions(["None", ...savedConfig.equipment_options]);
      if (savedConfig.category_options?.length) setCategoryOptions(savedConfig.category_options);
      if (savedConfig.tpv_options?.length) setTpvOptions(savedConfig.tpv_options);
      if (savedConfig.pv_options?.length) setPvOptions(savedConfig.pv_options);
      if (savedConfig.av_options?.length) setAvOptions(savedConfig.av_options);
      setInitialLoadDone(true);
    }
  }, [configLoaded, savedConfig, initialLoadDone]);

  // Auto-save when options change (only after initial load is done)
  useEffect(() => {
    if (!configLoaded || !initialLoadDone) return;
    saveConfig({
      rm_options: rmOptions.map(r => parseInt(r)),
      equipment_options: equipmentOptions.filter(e => e !== "None"),
      category_options: categoryOptions,
      tpv_options: tpvOptions,
      pv_options: pvOptions,
      av_options: avOptions,
    });
  }, [rmOptions, equipmentOptions, categoryOptions, tpvOptions, pvOptions, avOptions, configLoaded, initialLoadDone, saveConfig]);

  // Parse existing exercise data
  const parseRMFromName = (name) => {
    const match = name?.match(/(\d+)RM/);
    return match ? match[1] + "RM" : "";
  };
  
  const parseEquipmentFromName = (name) => {
    const options = ["Straight Bar", "Safety Squat Bar", "Dumbbell", "Cable"];
    for (const eq of options) {
      if (name?.includes(eq)) return eq;
    }
    return "";
  };

  // Compute initial equipment value
  const getInitialEquipment = () => {
    // Check if exercise has stored equipment
    if (exercise?.equipment && exercise.equipment.length > 0 && exercise.equipment[0] !== "none") {
      // Convert "straight_bar" to "Straight Bar"
      const stored = exercise.equipment[0];
      return stored
        .replace(/_/g, " ")
        .split(" ")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }
    // Try to parse from name
    const fromName = parseEquipmentFromName(exercise?.name);
    return fromName || "None";
  };

  const [formData, setFormData] = useState(() => ({
    name: exercise?.name || "",
    rm_value: exercise?.rm_value || parseRMFromName(exercise?.name) || "",
    equipment: getInitialEquipment(),
    movement_category: exercise?.movement_category || "squat",
    accel_velocity_params: exercise?.accel_velocity_params || [],
    maxes_config: exercise?.maxes_config || [{ type: "weight_lbs" }],
    video_url: exercise?.video_url || "",
    description: exercise?.description || "",
    difficulty: exercise?.difficulty || "intermediate",
    subtabs: exercise?.subtabs || [],
  }));
  const [saving, setSaving] = useState(false);
  const maxTypeOptions = [
    { value: "weight_lbs", label: "Weight (in lbs)" },
    { value: "time_secs", label: "Time (in Secs)" },
    { value: "speed_mph", label: "Speed (in MPH)" },
    { value: "resistance", label: "Resistance" },
    { value: "height_inches", label: "Height (in Inches)" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Handle "None" equipment as ["none"], otherwise convert to snake_case
      const equipmentValue = formData.equipment && formData.equipment !== "None"
        ? [formData.equipment.toLowerCase().replace(/ /g, "_")] 
        : ["none"];
      
      const response = await fetch(`${API}/coach/exercises/${exercise.exercise_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          category: "strength",
          exercise_type: "strength",
          equipment: equipmentValue,
          video_url: formData.video_url || null,
          difficulty: formData.difficulty,
          rm_value: formData.rm_value,
          movement_category: formData.movement_category,
          accel_velocity_params: formData.accel_velocity_params,
          maxes_config: formData.maxes_config,
          subtabs: formData.subtabs || [],
        })
      });

      if (response.ok) {
        toast.success("Exercise updated!");
        onSave({ exercise_id: exercise.exercise_id, name: formData.name });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update exercise");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const updateMaxConfig = (index, type) => {
    const updated = [...formData.maxes_config];
    updated[index] = { type };
    setFormData({ ...formData, maxes_config: updated });
  };

  const addMaxConfig = () => {
    setFormData({
      ...formData,
      maxes_config: [...formData.maxes_config, { type: "weight_lbs" }]
    });
  };

  const removeMaxConfig = (index) => {
    if (formData.maxes_config.length > 1) {
      setFormData({
        ...formData,
        maxes_config: formData.maxes_config.filter((_, i) => i !== index)
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Exercise Name */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <label className="block text-sm font-medium text-white mb-3">Exercise Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., 3RM Straight Bar Box Squat"
          className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white text-sm"
          required
        />
      </div>

      {/* RM Value */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <label className="block text-sm font-medium text-white mb-3">RM (Rep Max)</label>
        <div className="flex flex-wrap gap-2">
          {rmOptions.map((rm) => (
            <div key={rm} className="relative group inline-flex">
              <button type="button"
                onClick={() => setFormData({ ...formData, rm_value: rm })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.rm_value === rm ? "bg-primary text-white" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
              >{rm}</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setRmOptions(rmOptions.filter(r => r !== rm)); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
            </div>
          ))}
          <AddCustomInput
            onAdd={(val) => { const num = parseInt(val); if (num > 0) { const label = `${num}RM`; if (!rmOptions.includes(label)) setRmOptions([...rmOptions, label]); setFormData({ ...formData, rm_value: label }); } }}
            placeholder="e.g. 15"
            inputType="number"
          />
        </div>
      </div>

      {/* Equipment */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <label className="block text-sm font-medium text-white mb-3">Equipment</label>
        <div className="flex flex-wrap gap-2">
          {equipmentOptions.map((eq) => (
            <div key={eq} className="relative group inline-flex">
              <button type="button"
                onClick={() => setFormData({ ...formData, equipment: eq })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.equipment === eq ? "bg-lime-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
              >{eq}</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setEquipmentOptions(equipmentOptions.filter(o => o !== eq)); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
            </div>
          ))}
          <AddCustomInput
            onAdd={(val) => { if (val && !equipmentOptions.includes(val)) { setEquipmentOptions([...equipmentOptions, val]); setFormData({ ...formData, equipment: val }); } }}
            placeholder="e.g. Bands"
          />
        </div>
      </div>

      {/* Accel/Velocity Parameters */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <label className="block text-sm font-medium text-white mb-3">Accel/Velocity Parameters</label>
        <div className="space-y-4">
          {/* TpV */}
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">TpV (Time to Peak Velocity)</p>
            <div className="flex flex-wrap gap-2">
              {tpvOptions.map((ms) => {
                const tpvValue = `TpV ${ms}ms`;
                const isSelected = formData.accel_velocity_params.includes(tpvValue);
                return (
                  <div key={ms} className="relative group inline-flex">
                    <button type="button"
                      onClick={() => { const params = isSelected ? formData.accel_velocity_params.filter(p => p !== tpvValue) : [...formData.accel_velocity_params, tpvValue]; setFormData({ ...formData, accel_velocity_params: params }); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-lime-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                    >TpV {ms}ms</button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setTpvOptions(tpvOptions.filter(v => v !== ms)); setFormData({ ...formData, accel_velocity_params: formData.accel_velocity_params.filter(p => p !== tpvValue) }); }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                  </div>
                );
              })}
              <AddCustomInput onAdd={(val) => { const num = parseInt(val); if (num > 0 && !tpvOptions.includes(num)) setTpvOptions([...tpvOptions, num].sort((a, b) => b - a)); }} placeholder="ms" inputType="number" />
            </div>
          </div>
          {/* pV */}
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">pV (Peak Velocity)</p>
            <div className="flex flex-wrap gap-2">
              {pvOptions.map((val) => {
                const pvValue = `pV ${val}m/s`;
                const isSelected = formData.accel_velocity_params.includes(pvValue);
                return (
                  <div key={val} className="relative group inline-flex">
                    <button type="button"
                      onClick={() => { const params = isSelected ? formData.accel_velocity_params.filter(p => p !== pvValue) : [...formData.accel_velocity_params, pvValue]; setFormData({ ...formData, accel_velocity_params: params }); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-cyan-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                    >pV {val}m/s</button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPvOptions(pvOptions.filter(v => v !== val)); setFormData({ ...formData, accel_velocity_params: formData.accel_velocity_params.filter(p => p !== pvValue) }); }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                  </div>
                );
              })}
              <AddCustomInput onAdd={(val) => { const num = parseFloat(val); if (num > 0 && !pvOptions.includes(num)) setPvOptions([...pvOptions, num].sort((a, b) => a - b)); }} placeholder="m/s" inputType="number" />
            </div>
          </div>
          {/* aV */}
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">aV (Average Velocity)</p>
            <div className="flex flex-wrap gap-2">
              {avOptions.map((val) => {
                const avValue = `aV ${val}m/s`;
                const isSelected = formData.accel_velocity_params.includes(avValue);
                return (
                  <div key={val} className="relative group inline-flex">
                    <button type="button"
                      onClick={() => { const params = isSelected ? formData.accel_velocity_params.filter(p => p !== avValue) : [...formData.accel_velocity_params, avValue]; setFormData({ ...formData, accel_velocity_params: params }); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-orange-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                    >aV {val}m/s</button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setAvOptions(avOptions.filter(v => v !== val)); setFormData({ ...formData, accel_velocity_params: formData.accel_velocity_params.filter(p => p !== avValue) }); }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                  </div>
                );
              })}
              <AddCustomInput onAdd={(val) => { const num = parseFloat(val); if (num > 0 && !avOptions.includes(num)) setAvOptions([...avOptions, num].sort((a, b) => a - b)); }} placeholder="m/s" inputType="number" />
            </div>
          </div>
        </div>
      </div>

      {/* Number of Maxes */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-white">Number of Maxes</label>
          <button
            type="button"
            onClick={addMaxConfig}
            className="text-xs bg-primary/20 text-primary px-3 py-1.5 rounded-lg hover:bg-primary/30"
          >
            <Plus className="w-3 h-3 inline mr-1" /> Add Max
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {formData.maxes_config.map((config, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">#{i + 1}</span>
              <select
                value={config.type}
                onChange={(e) => updateMaxConfig(i, e.target.value)}
                className="flex-1 bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white"
              >
                {maxTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {formData.maxes_config.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeMaxConfig(i)}
                  className="p-2 hover:bg-[#1E293B] rounded-lg text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>


      {/* Video URL */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <label className="block text-sm font-medium text-white mb-3">Video URL</label>
        <input
          type="url"
          value={formData.video_url}
          onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
          placeholder="https://youtube.com/..."
          className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white text-sm"
        />
      </div>

      {/* Category (Movement Pattern) */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <label className="block text-sm font-medium text-white mb-3">Category (Movement Pattern)</label>
        <div className="flex flex-wrap gap-2">
          {categoryOptions.map((cat) => (
            <div key={cat} className="relative group inline-flex">
              <button type="button"
                onClick={() => setFormData({ ...formData, movement_category: cat.toLowerCase().replace(/ /g, "_") })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.movement_category === cat.toLowerCase().replace(/ /g, "_") ? "bg-primary text-white" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
              >{cat}</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setCategoryOptions(categoryOptions.filter(c => c !== cat)); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
            </div>
          ))}
          <AddCustomInput
            onAdd={(val) => { if (val && !categoryOptions.includes(val)) { setCategoryOptions([...categoryOptions, val]); setFormData({ ...formData, movement_category: val.toLowerCase().replace(/ /g, "_") }); } }}
            placeholder="e.g. Olympic"
          />
        </div>
      </div>

      {/* Difficulty */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <label className="block text-sm font-medium text-white mb-3">Difficulty</label>
        <div className="flex gap-4">
          {["beginner", "intermediate", "advanced"].map((diff) => (
            <button
              key={diff}
              type="button"
              onClick={() => setFormData({ ...formData, difficulty: diff })}
              className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                formData.difficulty === diff
                  ? "bg-primary text-white"
                  : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"
              }`}
            >
              {diff}
            </button>
          ))}
        </div>
      </div>

      {/* Subtabs */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <SubtabSelector
          libraryType="exercises"
          category="strength"
          selectedSubtabs={formData.subtabs}
          onChange={(subtabs) => setFormData({ ...formData, subtabs })}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-4 pt-4 border-t border-[#1E293B]">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
        >
          {saving ? "Saving..." : "Update Exercise"}
        </button>
      </div>
    </form>
  );
};

// ==================== SPEED/POWER BATCH FORM ====================
const SpeedPowerBatchForm = ({ onClose, onSave, onDuplicate }) => {
  const { config: savedConfig, saveConfig, loaded: configLoaded } = useExerciseFormConfig("speed_power");
  const [exerciseNames, setExerciseNames] = useState([""]);
  const [selectedFlyIns, setSelectedFlyIns] = useState([]);
  const [selectedStances, setSelectedStances] = useState([]);
  const [numberOfMaxes, setNumberOfMaxes] = useState(1);
  const [maxesConfig, setMaxesConfig] = useState([{ type: "speed" }]);
  const [movementCategory, setMovementCategory] = useState("sprint");
  const [namingOrder, setNamingOrder] = useState(1);
  const [namingConfig, setNamingConfig] = useState([{ section: "fly_in" }]);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable options - loaded from saved config
  const [flyInOptions, setFlyInOptions] = useState(SPEED_POWER_DEFAULTS.fly_in_options);
  const [stanceOptions, setStanceOptions] = useState(SPEED_POWER_DEFAULTS.stance_options);
  const [categoryOptions, setCategoryOptions] = useState(SPEED_POWER_DEFAULTS.category_options);

  // Load saved config
  useEffect(() => {
    if (configLoaded && savedConfig) {
      if (savedConfig.fly_in_options?.length) setFlyInOptions(savedConfig.fly_in_options);
      if (savedConfig.stance_options?.length) setStanceOptions(savedConfig.stance_options);
      if (savedConfig.category_options?.length) setCategoryOptions(savedConfig.category_options);
    }
  }, [configLoaded, savedConfig]);

  // Auto-save when options change
  useEffect(() => {
    if (!configLoaded) return;
    saveConfig({
      fly_in_options: flyInOptions,
      stance_options: stanceOptions,
      category_options: categoryOptions,
    });
  }, [flyInOptions, stanceOptions, categoryOptions]);

  // Max type options for Speed/Power
  const maxTypeOptions = [
    { value: "speed", label: "Speed" },
    { value: "height", label: "Height" },
    { value: "length", label: "Length" },
    { value: "resistance", label: "Resistance" },
    { value: "weight", label: "Weight" },
    { value: "time", label: "Time" },
  ];

  // Naming sections for order of naming
  const namingSections = [
    { value: "fly_in", label: "Fly In" },
    { value: "stance", label: "Stance" },
    { value: "exercise_name", label: "Exercise Name" },
    { value: "category", label: "Category" },
  ];

  // Toggle checkbox selection
  const toggleSelection = (arr, setArr, value) => {
    if (arr.includes(value)) {
      setArr(arr.filter(v => v !== value));
    } else {
      setArr([...arr, value]);
    }
  };

  // Add exercise name field
  const addExerciseName = () => {
    setExerciseNames([...exerciseNames, ""]);
  };

  // Update exercise name
  const updateExerciseName = (index, value) => {
    const updated = [...exerciseNames];
    updated[index] = value;
    setExerciseNames(updated);
  };

  // Remove exercise name
  const removeExerciseName = (index) => {
    if (exerciseNames.length > 1) {
      setExerciseNames(exerciseNames.filter((_, i) => i !== index));
    }
  };

  // Update maxes config when number changes
  const handleMaxesCountChange = (count) => {
    setNumberOfMaxes(count);
    const newConfig = Array.from({ length: count }, (_, i) => 
      maxesConfig[i] || { type: "speed" }
    );
    setMaxesConfig(newConfig);
  };

  // Update naming config when order count changes
  const handleNamingOrderChange = (count) => {
    setNamingOrder(count);
    const newConfig = Array.from({ length: count }, (_, i) => 
      namingConfig[i] || { section: namingSections[i]?.value || "fly_in" }
    );
    setNamingConfig(newConfig);
  };

  // Generate exercise name based on naming order
  const generateExerciseName = (baseName, flyIn, stance) => {
    const parts = [];
    namingConfig.forEach(config => {
      switch (config.section) {
        case "fly_in":
          if (flyIn) parts.push(`${flyIn} yard fly`);
          break;
        case "stance":
          if (stance) {
            const stanceLabel = stanceOptions.find(s => s.value === stance)?.label || stance;
            parts.push(stanceLabel);
          }
          break;
        case "exercise_name":
          if (baseName) parts.push(baseName);
          break;
        case "category":
          parts.push(movementCategory.charAt(0).toUpperCase() + movementCategory.slice(1));
          break;
        default:
          break;
      }
    });
    return parts.join(" ");
  };

  // Generate all exercise combinations for preview
  const generateExercises = () => {
    const exercises = [];
    const validNames = exerciseNames.filter(n => n.trim());
    const flyIns = selectedFlyIns.length > 0 ? selectedFlyIns : [null];
    const stances = selectedStances.length > 0 ? selectedStances : [null];

    validNames.forEach(baseName => {
      flyIns.forEach(flyIn => {
        stances.forEach(stance => {
          const name = generateExerciseName(baseName, flyIn, stance);
          exercises.push({
            name,
            base_name: baseName,
            fly_in: flyIn ? `${flyIn} yard fly` : null,
            stance: stance || "None",
            movement_category: movementCategory,
            maxes_config: maxesConfig,
            category: "speed_power",
            exercise_type: "speed",
          });
        });
      });
    });

    return exercises;
  };

  // Handle preview click
  const handlePreview = () => {
    const validNames = exerciseNames.filter(n => n.trim());
    if (validNames.length === 0) {
      toast.error("Please add at least one exercise name");
      return;
    }
    setShowPreview(true);
  };

  // Handle create exercises
  const handleCreate = async () => {
    setSaving(true);
    const exercises = generateExercises();
    
    try {
      let successCount = 0;
      let duplicateCount = 0;
      let firstCreated = null;
      
      for (const exercise of exercises) {
        const exerciseData = {
          name: exercise.name,
          description: `${exercise.movement_category} exercise`,
          category: "speed_power",
          exercise_type: "speed",
          equipment: ["none"],
          video_url: null,
          muscle_groups: [],
          difficulty: "intermediate",
          fly_in: exercise.fly_in,
          stance: exercise.stance,
          movement_category: exercise.movement_category,
          maxes_config: exercise.maxes_config,
        };
        
        const response = await fetch(`${API}/coach/exercises`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(exerciseData)
        });
        
        if (response.ok) {
          successCount++;
          if (!firstCreated) {
            const created = await response.json();
            firstCreated = { exercise_id: created.exercise_id, name: created.name, rm_value: created.rm_value, bar_equipment_id: created.bar_equipment_id };
          }
        } else if (response.status === 409) {
          // Duplicate found
          duplicateCount++;
          const errorData = await response.json();
          if (!firstCreated && errorData.detail?.existing_exercise) {
            firstCreated = { exercise_id: errorData.detail.existing_exercise.exercise_id, name: errorData.detail.existing_exercise.name, rm_value: errorData.detail.existing_exercise.rm_value, bar_equipment_id: errorData.detail.existing_exercise.bar_equipment_id };
          }
          if (onDuplicate && errorData.detail?.existing_exercise) {
            // Show modal for the first duplicate, skip others
            if (duplicateCount === 1) {
              onDuplicate(exerciseData, errorData.detail.existing_exercise);
            }
          }
        }
      }
      
      if (successCount > 0) {
        toast.success(`Created ${successCount} exercise${successCount > 1 ? 's' : ''}!`);
      }
      if (duplicateCount > 0 && successCount > 0) {
        toast.warning(`${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''} skipped`);
      }
      
      setShowPreview(false);
      onSave(firstCreated);
    } catch (error) {
      toast.error("Failed to create exercises");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Exercise Name(s) Section */}
        <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-white">Exercise Name(s)</label>
            <button
              type="button"
              onClick={addExerciseName}
              className="flex items-center gap-1 text-xs bg-lime-400/20 text-lime-300 px-3 py-1.5 rounded-lg hover:bg-lime-400/30 transition-colors"
            >
              <Plus className="w-3 h-3" /> Add Exercise
            </button>
          </div>
          <div className="space-y-2">
            {exerciseNames.map((name, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => updateExerciseName(i, e.target.value)}
                  placeholder="e.g., Sprint Start"
                  className="flex-1 bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white text-sm"
                  data-testid={`speed-exercise-name-input-${i}`}
                />
                {exerciseNames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeExerciseName(i)}
                    className="p-2.5 hover:bg-[#1E293B] rounded-lg text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Fly Ins Section */}
        <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
          <label className="block text-sm font-medium text-white mb-3">Fly Ins</label>
          <div className="flex flex-wrap gap-2">
            {flyInOptions.map((yards) => (
              <div key={yards} className="relative group inline-flex">
                <button type="button"
                  onClick={() => toggleSelection(selectedFlyIns, setSelectedFlyIns, yards)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedFlyIns.includes(yards) ? "bg-lime-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                  data-testid={`fly-in-btn-${yards}`}
                >{yards} yard fly</button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setFlyInOptions(flyInOptions.filter(v => v !== yards)); setSelectedFlyIns(selectedFlyIns.filter(v => v !== yards)); }}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
              </div>
            ))}
            <AddCustomInput
              onAdd={(val) => { const num = parseInt(val); if (num > 0 && !flyInOptions.includes(num)) setFlyInOptions([...flyInOptions, num].sort((a, b) => a - b)); }}
              placeholder="yards"
              inputType="number"
            />
          </div>
        </div>

        {/* Stance Section */}
        <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
          <label className="block text-sm font-medium text-white mb-3">Stance</label>
          <div className="flex flex-wrap gap-2">
            {stanceOptions.map((stance) => (
              <div key={stance.value} className="relative group inline-flex">
                <button type="button"
                  onClick={() => toggleSelection(selectedStances, setSelectedStances, stance.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedStances.includes(stance.value) ? "bg-lime-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                  data-testid={`stance-btn-${stance.value}`}
                >{stance.label}</button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setStanceOptions(stanceOptions.filter(s => s.value !== stance.value)); setSelectedStances(selectedStances.filter(s => s !== stance.value)); }}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
              </div>
            ))}
            <AddCustomInput
              onAdd={(val) => { if (val) { const key = val.toLowerCase().replace(/ /g, "_"); if (!stanceOptions.find(s => s.value === key)) setStanceOptions([...stanceOptions, { value: key, label: val }]); } }}
              placeholder="e.g. Split Stance"
            />
          </div>
        </div>

        {/* Number of Maxes Section */}
        <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-white">Number of Maxes</label>
            <NumberInput 
              value={numberOfMaxes} 
              onChange={handleMaxesCountChange} 
              min={1} 
              max={10} 
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {maxesConfig.map((config, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-8">#{i + 1}</span>
                <select
                  value={config.type}
                  onChange={(e) => {
                    const updated = [...maxesConfig];
                    updated[i] = { type: e.target.value };
                    setMaxesConfig(updated);
                  }}
                  className="flex-1 bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white"
                >
                  {maxTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Category Section */}
        <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
          <label className="block text-sm font-medium text-white mb-3">Category</label>
          <div className="flex flex-wrap gap-2">
            {categoryOptions.map((cat) => (
              <div key={cat} className="relative group inline-flex">
                <button type="button"
                  onClick={() => setMovementCategory(cat.toLowerCase())}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${movementCategory === cat.toLowerCase() ? "bg-primary text-white" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                >{cat}</button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setCategoryOptions(categoryOptions.filter(c => c !== cat)); }}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
              </div>
            ))}
            <AddCustomInput
              onAdd={(val) => { if (val && !categoryOptions.includes(val)) { setCategoryOptions([...categoryOptions, val]); setMovementCategory(val.toLowerCase()); } }}
              placeholder="e.g. Agility"
            />
          </div>
        </div>

        {/* Order of Naming Section */}
        <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-white">Order of Naming</label>
            <NumberInput 
              value={namingOrder} 
              onChange={handleNamingOrderChange} 
              min={1} 
              max={4} 
            />
          </div>
          <p className="text-xs text-gray-500 mb-3">Select the order of how exercise names will be generated</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {namingConfig.map((config, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-8">#{i + 1}</span>
                <select
                  value={config.section}
                  onChange={(e) => {
                    const updated = [...namingConfig];
                    updated[i] = { section: e.target.value };
                    setNamingConfig(updated);
                  }}
                  className="flex-1 bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white"
                >
                  {namingSections.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-[#141B2D] rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Example output:</p>
            <p className="text-sm text-lime-300 font-medium">
              {generateExerciseName(
                exerciseNames[0] || "Sprint Start",
                selectedFlyIns[0] || 10,
                selectedStances[0] || "2_point"
              )}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4 border-t border-[#1E293B]">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePreview}
            className="flex-1 bg-[#2D3B4F] text-white py-3 rounded-lg font-semibold hover:bg-[#374B69] transition-colors flex items-center justify-center gap-2"
          >
            <Eye className="w-5 h-5" /> Preview
          </button>
          <button
            type="button"
            onClick={handlePreview}
            className="flex-1 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
          >
            Create Exercises
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && (
          <PreviewModal
            exercises={generateExercises()}
            onClose={() => setShowPreview(false)}
            onConfirm={handleCreate}
            saving={saving}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== EXERCISE FORM ====================
const ExerciseForm = ({ exercise, muscleGroups, equipment, onClose, onSave, onDuplicate }) => {
  // Determine initial category based on exercise_type
  const getInitialCategory = () => {
    if (exercise?.exercise_type) {
      if (["plyometric", "cardio", "speed", "power"].includes(exercise.exercise_type)) {
        return "speed_power";
      }
    }
    return "strength";
  };

  const [activeCategory, setActiveCategory] = useState(getInitialCategory());

  // For editing existing exercises, use the old form
  const isEditing = !!exercise;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#141B2D] border-b border-[#1E293B] p-6 flex justify-between items-center z-10">
          <h2 className="font-heading text-2xl font-bold">
            {exercise ? "Edit Exercise" : "Create Exercises"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Category Tabs - Strength vs Speed/Power */}
          <div className="flex gap-2 p-1 bg-[#0B0F1A] rounded-xl mb-6">
            <button
              type="button"
              onClick={() => setActiveCategory("strength")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all ${
                activeCategory === "strength"
                  ? "bg-primary text-white"
                  : "text-gray-400 hover:text-white hover:bg-[#1E293B]"
              }`}
              data-testid="category-tab-strength"
            >
              <Dumbbell className="w-5 h-5" />
              Strength
            </button>
            <button
              type="button"
              onClick={() => setActiveCategory("speed_power")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all ${
                activeCategory === "speed_power"
                  ? "bg-lime-400 text-black"
                  : "text-gray-400 hover:text-white hover:bg-[#1E293B]"
              }`}
              data-testid="category-tab-speed-power"
            >
              <Zap className="w-5 h-5" />
              Speed/Power
            </button>
          </div>

          {/* Render appropriate form based on category and mode */}
          {activeCategory === "strength" ? (
            isEditing ? (
              <StrengthEditForm exercise={exercise} onClose={onClose} onSave={onSave} />
            ) : (
              <StrengthBatchForm onClose={onClose} onSave={onSave} onDuplicate={onDuplicate} />
            )
          ) : (
            isEditing ? (
              <SpeedPowerEditForm 
                exercise={exercise} 
                muscleGroups={muscleGroups} 
                equipment={equipment} 
                onClose={onClose} 
                onSave={onSave}
                category={activeCategory}
              />
            ) : (
              <SpeedPowerBatchForm onClose={onClose} onSave={onSave} onDuplicate={onDuplicate} />
            )
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== SPEED/POWER EDIT FORM (matches creation form structure) ====================
const SpeedPowerEditForm = ({ exercise, onClose, onSave }) => {
  // Parse existing exercise data to extract fly-in and stance
  const parseFlyIn = (name) => {
    const match = name?.match(/(\d+)\s*yard/i);
    return match ? parseInt(match[1]) : null;
  };
  
  const parseStance = (name) => {
    if (name?.includes("2 Point Sideways")) return "2_point_sideways";
    if (name?.includes("4 Point")) return "4_point";
    if (name?.includes("3 Point")) return "3_point";
    if (name?.includes("2 Point")) return "2_point";
    return "";
  };

  const [formData, setFormData] = useState({
    name: exercise?.name || "",
    fly_in: exercise?.fly_in || parseFlyIn(exercise?.name) || "",
    stance: exercise?.stance || parseStance(exercise?.name) || "",
    movement_category: exercise?.movement_category || "sprint",
    maxes_config: exercise?.maxes_config || [{ type: "speed" }],
    accel_velocity_params: exercise?.accel_velocity_params || [],
    video_url: exercise?.video_url || "",
    description: exercise?.description || "",
    difficulty: exercise?.difficulty || "intermediate",
    subtabs: exercise?.subtabs || [],
  });
  const [saving, setSaving] = useState(false);
  const { config: savedConfig, saveConfig, loaded: configLoaded } = useExerciseFormConfig("speed_power");

  // Editable options - loaded from saved config
  const [flyInOptions, setFlyInOptions] = useState(SPEED_POWER_DEFAULTS.fly_in_options);
  const [stanceOptions, setStanceOptions] = useState(SPEED_POWER_DEFAULTS.stance_options);
  const [tpvOptions, setTpvOptions] = useState(SPEED_POWER_DEFAULTS.tpv_options);
  const [pvOptions, setPvOptions] = useState(SPEED_POWER_DEFAULTS.pv_options);
  const [avOptions, setAvOptions] = useState(SPEED_POWER_DEFAULTS.av_options);
  const [movementCategories, setMovementCategories] = useState(SPEED_POWER_DEFAULTS.movement_categories);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Load saved config (only once on initial load)
  useEffect(() => {
    if (configLoaded && savedConfig && !initialLoadDone) {
      if (savedConfig.fly_in_options?.length) setFlyInOptions(savedConfig.fly_in_options);
      if (savedConfig.stance_options?.length) setStanceOptions(savedConfig.stance_options);
      if (savedConfig.tpv_options?.length) setTpvOptions(savedConfig.tpv_options);
      if (savedConfig.pv_options?.length) setPvOptions(savedConfig.pv_options);
      if (savedConfig.av_options?.length) setAvOptions(savedConfig.av_options);
      if (savedConfig.movement_categories?.length) setMovementCategories(savedConfig.movement_categories);
      setInitialLoadDone(true);
    }
  }, [configLoaded, savedConfig, initialLoadDone]);

  // Auto-save when options change (only after initial load is done)
  useEffect(() => {
    if (!configLoaded || !initialLoadDone) return;
    saveConfig({
      fly_in_options: flyInOptions,
      stance_options: stanceOptions,
      tpv_options: tpvOptions,
      pv_options: pvOptions,
      av_options: avOptions,
      movement_categories: movementCategories,
    });
  }, [flyInOptions, stanceOptions, tpvOptions, pvOptions, avOptions, movementCategories, configLoaded, initialLoadDone, saveConfig]);

  // Max type options for Speed/Power
  const maxTypeOptions = [
    { value: "speed", label: "Speed (time in secs)" },
    { value: "speed_mph", label: "Speed (MPH)" },
    { value: "distance", label: "Distance (yards/meters)" },
    { value: "height", label: "Height (inches)" },
    { value: "power", label: "Power (watts)" },
    { value: "reps", label: "Reps" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API}/coach/exercises/${exercise.exercise_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          category: "speed_power",
          exercise_type: "speed",
          equipment: ["none"],
          video_url: formData.video_url || null,
          difficulty: formData.difficulty,
          fly_in: formData.fly_in,
          stance: formData.stance,
          movement_category: formData.movement_category,
          maxes_config: formData.maxes_config,
          accel_velocity_params: formData.accel_velocity_params,
          subtabs: formData.subtabs || [],
        })
      });

      if (response.ok) {
        toast.success("Exercise updated!");
        onSave({ exercise_id: exercise.exercise_id, name: formData.name });
      } else {
        toast.error("Failed to update exercise");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const updateMaxType = (index, newType) => {
    const newConfig = [...formData.maxes_config];
    newConfig[index] = { type: newType };
    setFormData({ ...formData, maxes_config: newConfig });
  };

  const addMaxType = () => {
    if (formData.maxes_config.length < 3) {
      setFormData({
        ...formData,
        maxes_config: [...formData.maxes_config, { type: "speed" }]
      });
    }
  };

  const removeMaxType = (index) => {
    if (formData.maxes_config.length > 1) {
      setFormData({
        ...formData,
        maxes_config: formData.maxes_config.filter((_, i) => i !== index)
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Exercise Name */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Exercise Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
          required
        />
      </div>

      {/* Fly-In Distance */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Fly-In Distance (yards)</label>
        <div className="flex flex-wrap gap-2">
          {flyInOptions.map((dist) => (
            <div key={dist} className="relative group inline-flex">
              <button type="button"
                onClick={() => setFormData({ ...formData, fly_in: formData.fly_in === dist ? "" : dist })}
                className={`px-4 py-2 rounded-lg transition-colors ${formData.fly_in === dist ? "bg-lime-400 text-black font-semibold" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
              >{dist} yd</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setFlyInOptions(flyInOptions.filter(v => v !== dist)); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
            </div>
          ))}
          <AddCustomInput
            onAdd={(val) => { const num = parseInt(val); if (num > 0 && !flyInOptions.includes(num)) { setFlyInOptions([...flyInOptions, num].sort((a, b) => a - b)); setFormData({ ...formData, fly_in: num }); } }}
            placeholder="yards"
            inputType="number"
          />
        </div>
      </div>

      {/* Stance */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Stance</label>
        <div className="flex flex-wrap gap-2">
          {stanceOptions.map((stance) => (
            <div key={stance.value} className="relative group inline-flex">
              <button type="button"
                onClick={() => setFormData({ ...formData, stance: formData.stance === stance.value ? "" : stance.value })}
                className={`px-4 py-2 rounded-lg transition-colors ${formData.stance === stance.value ? "bg-lime-400 text-black font-semibold" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
              >{stance.label}</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setStanceOptions(stanceOptions.filter(s => s.value !== stance.value)); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
            </div>
          ))}
          <AddCustomInput
            onAdd={(val) => { if (val) { const key = val.toLowerCase().replace(/ /g, "_"); if (!stanceOptions.find(s => s.value === key)) { setStanceOptions([...stanceOptions, { value: key, label: val }]); setFormData({ ...formData, stance: key }); } } }}
            placeholder="e.g. Split"
          />
        </div>
      </div>

      {/* Movement Category */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Movement Category</label>
        <div className="flex flex-wrap gap-2">
          {movementCategories.map((cat) => (
            <div key={cat.value} className="relative group inline-flex">
              <button type="button"
                onClick={() => setFormData({ ...formData, movement_category: cat.value })}
                className={`px-4 py-2 rounded-lg transition-colors ${formData.movement_category === cat.value ? "bg-primary text-white font-semibold" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
              >{cat.label}</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setMovementCategories(movementCategories.filter(c => c.value !== cat.value)); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
            </div>
          ))}
          <AddCustomInput
            onAdd={(val) => { if (val) { const key = val.toLowerCase().replace(/ /g, "_"); if (!movementCategories.find(c => c.value === key)) { setMovementCategories([...movementCategories, { value: key, label: val }]); setFormData({ ...formData, movement_category: key }); } } }}
            placeholder="e.g. Lateral"
          />
        </div>
      </div>

      {/* Max Types */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-gray-400">Max Type(s) to Track</label>
          {formData.maxes_config.length < 3 && (
            <button
              type="button"
              onClick={addMaxType}
              className="text-xs text-lime-300 hover:underline"
            >
              + Add Another Max
            </button>
          )}
        </div>

      {/* Accel/Velocity Parameters */}
      <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
        <label className="block text-sm font-medium text-white mb-3">Accel/Velocity Parameters</label>
        <div className="space-y-4">
          {/* TpV */}
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">TpV (Time to Peak Velocity)</p>
            <div className="flex flex-wrap gap-2">
              {tpvOptions.map((ms) => {
                const tpvValue = `TpV ${ms}ms`;
                const isSelected = formData.accel_velocity_params.includes(tpvValue);
                return (
                  <div key={ms} className="relative group inline-flex">
                    <button type="button"
                      onClick={() => { const params = isSelected ? formData.accel_velocity_params.filter(p => p !== tpvValue) : [...formData.accel_velocity_params, tpvValue]; setFormData({ ...formData, accel_velocity_params: params }); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-lime-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                    >TpV {ms}ms</button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setTpvOptions(tpvOptions.filter(v => v !== ms)); setFormData({ ...formData, accel_velocity_params: formData.accel_velocity_params.filter(p => p !== tpvValue) }); }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                  </div>
                );
              })}
              <AddCustomInput onAdd={(val) => { const num = parseInt(val); if (num > 0 && !tpvOptions.includes(num)) setTpvOptions([...tpvOptions, num].sort((a, b) => b - a)); }} placeholder="ms" inputType="number" />
            </div>
          </div>
          {/* pV */}
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">pV (Peak Velocity)</p>
            <div className="flex flex-wrap gap-2">
              {pvOptions.map((val) => {
                const pvValue = `pV ${val}m/s`;
                const isSelected = formData.accel_velocity_params.includes(pvValue);
                return (
                  <div key={val} className="relative group inline-flex">
                    <button type="button"
                      onClick={() => { const params = isSelected ? formData.accel_velocity_params.filter(p => p !== pvValue) : [...formData.accel_velocity_params, pvValue]; setFormData({ ...formData, accel_velocity_params: params }); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-cyan-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                    >pV {val}m/s</button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPvOptions(pvOptions.filter(v => v !== val)); setFormData({ ...formData, accel_velocity_params: formData.accel_velocity_params.filter(p => p !== pvValue) }); }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                  </div>
                );
              })}
              <AddCustomInput onAdd={(val) => { const num = parseFloat(val); if (num > 0 && !pvOptions.includes(num)) setPvOptions([...pvOptions, num].sort((a, b) => a - b)); }} placeholder="m/s" inputType="number" />
            </div>
          </div>
          {/* aV */}
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">aV (Average Velocity)</p>
            <div className="flex flex-wrap gap-2">
              {avOptions.map((val) => {
                const avValue = `aV ${val}m/s`;
                const isSelected = formData.accel_velocity_params.includes(avValue);
                return (
                  <div key={val} className="relative group inline-flex">
                    <button type="button"
                      onClick={() => { const params = isSelected ? formData.accel_velocity_params.filter(p => p !== avValue) : [...formData.accel_velocity_params, avValue]; setFormData({ ...formData, accel_velocity_params: params }); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? "bg-orange-400 text-black" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"}`}
                    >aV {val}m/s</button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setAvOptions(avOptions.filter(v => v !== val)); setFormData({ ...formData, accel_velocity_params: formData.accel_velocity_params.filter(p => p !== avValue) }); }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] leading-none opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10" title="Remove">x</button>
                  </div>
                );
              })}
              <AddCustomInput onAdd={(val) => { const num = parseFloat(val); if (num > 0 && !avOptions.includes(num)) setAvOptions([...avOptions, num].sort((a, b) => a - b)); }} placeholder="m/s" inputType="number" />
            </div>
          </div>
        </div>
      </div>
        <div className="space-y-2">
          {formData.maxes_config.map((config, index) => (
            <div key={index} className="flex items-center gap-2">
              <select
                value={config.type}
                onChange={(e) => updateMaxType(index, e.target.value)}
                className="flex-1 bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2 text-white"
              >
                {maxTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {formData.maxes_config.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeMaxType(index)}
                  className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Video URL */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Video URL (optional)</label>
        <input
          type="url"
          value={formData.video_url}
          onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
          className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white"
          placeholder="https://youtube.com/..."
        />
      </div>

      {/* Difficulty */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Difficulty</label>
        <div className="flex gap-2">
          {["beginner", "intermediate", "advanced"].map((diff) => (
            <button
              key={diff}
              type="button"
              onClick={() => setFormData({ ...formData, difficulty: diff })}
              className={`flex-1 py-2 rounded-lg capitalize transition-colors ${
                formData.difficulty === diff
                  ? "bg-primary text-white"
                  : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"
              }`}
            >
              {diff}
            </button>
          ))}
        </div>
      </div>

      {/* Subtabs */}
      <SubtabSelector
        libraryType="exercises"
        category="speed_power"
        selectedSubtabs={formData.subtabs}
        onChange={(subtabs) => setFormData({ ...formData, subtabs })}
      />

      {/* Submit Buttons */}
      <div className="flex gap-4 pt-4 border-t border-[#1E293B]">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-50"
        >
          {saving ? "Saving..." : "Update Exercise"}
        </button>
      </div>
    </form>
  );
};

export default ExerciseLibrary;
export { ExerciseForm };
